import { useState, useEffect } from "react";
import dayjs from "dayjs";
import { CirclePicker } from "react-color";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import FormControl from "@mui/material/FormControl";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CloseIcon from "@mui/icons-material/Close";

const defaultColor = "#00953d";
const eventColors = [
    "#00953d",
    "#2e7d32",
    "#1976d2",
    "#0288d1",
    "#7b1fa2",
    "#c2185b",
    "#d32f2f",
    "#ed6c02",
    "#f9a825",
    "#6d4c41",
    "#546e7a",
    "#455a64",
];

export default function NewEventDialog(props) {
    const [name, setName] = useState("");
    const [color, setColor] = useState(defaultColor);
    const [selectedCalendarKey, setSelectedCalendarKey] = useState("");

    useEffect(() => {
        if (!props.open) {
            return;
        }

        const calendarStillAvailable = (props.calendars || []).some(
            (calendar) => calendar.key === selectedCalendarKey,
        );

        if (!calendarStillAvailable) {
            setSelectedCalendarKey(props.calendars?.[0]?.key || "");
        }
    }, [props.open, props.calendars, selectedCalendarKey]);

    const resetForm = () => {
        setName("");
        setColor(defaultColor);
        setSelectedCalendarKey(props.calendars?.[0]?.key || "");
    };

    const handleClose = () => {
        resetForm();
        props.onClose();
    };

    const handleAddClick = () => {
        const selectedCalendar = (props.calendars || []).find(
            (calendar) => calendar.key === selectedCalendarKey,
        );

        if (name.trim() === "" || !selectedCalendar) {
            return;
        }

        props.addEvent(name.trim(), selectedCalendar, color);
        resetForm();
    };

    const canCreate = name.trim() !== "" && selectedCalendarKey !== "";

    return (
        <Dialog
            onClose={handleClose}
            aria-labelledby="new-event-dialog-title"
            open={props.open}
            fullWidth
            maxWidth="xs"
            slotProps={{
                paper: {
                    sx: {
                        borderRadius: 3,
                        overflow: "hidden",
                        backgroundColor: "background.paper",
                    },
                },
            }}
        >
            <Box
                sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1.5,
                    px: { xs: 2, sm: 3 },
                    py: 2.5,
                }}
            >
                <Box
                    sx={{
                        width: 42,
                        height: 42,
                        borderRadius: 2,
                        display: "grid",
                        placeItems: "center",
                        flexShrink: 0,
                        color: "primary.main",
                        backgroundColor: "rgba(0, 149, 61, 0.09)",
                    }}
                >
                    <CalendarMonthOutlinedIcon />
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                        id="new-event-dialog-title"
                        variant="h5"
                        component="h2"
                        sx={{ lineHeight: 1.1 }}
                    >
                        New event
                    </Typography>
                    <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ mt: 0.5 }}
                    >
                        {props.date
                            ? dayjs(props.date).format("dddd, D MMMM YYYY")
                            : "Choose the event details."}
                    </Typography>
                </Box>
                <IconButton
                    aria-label="Close new event"
                    onClick={handleClose}
                    sx={{ mt: -0.75, mr: -1 }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>

            <DialogContent
                dividers
                sx={{
                    p: { xs: 2, sm: 2.5 },
                    backgroundColor: "background.default",
                }}
            >
                <Box
                    sx={{
                        p: { xs: 2, sm: 2.5 },
                        borderRadius: 2.5,
                        border: "1px solid",
                        borderColor: "divider",
                        backgroundColor: "background.paper",
                    }}
                >
                    <Typography
                        variant="subtitle2"
                        sx={{ mb: 1.5, fontWeight: 800 }}
                    >
                        Event details
                    </Typography>
                    <TextField
                        autoFocus
                        id="new-event-name"
                        label="Event name"
                        variant="outlined"
                        value={name}
                        onChange={(event) => setName(event.target.value)}
                        onKeyDown={(event) => {
                            if (event.key === "Enter" && canCreate) {
                                event.preventDefault();
                                handleAddClick();
                            }
                        }}
                        fullWidth
                        sx={{
                            mb: 2,
                            "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                            },
                        }}
                    />
                    <FormControl fullWidth>
                        <InputLabel id="new-event-calendar-label">
                            Calendar
                        </InputLabel>
                        <Select
                            labelId="new-event-calendar-label"
                            id="new-event-calendar"
                            value={selectedCalendarKey}
                            label="Calendar"
                            onChange={(event) =>
                                setSelectedCalendarKey(event.target.value)
                            }
                            disabled={(props.calendars || []).length === 0}
                            sx={{ borderRadius: 2 }}
                        >
                            {(props.calendars || []).map((calendar) => (
                                <MenuItem
                                    key={calendar.key}
                                    value={calendar.key}
                                >
                                    {calendar.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {(props.calendars || []).length === 0 && (
                        <Typography
                            variant="caption"
                            color="error"
                            sx={{ display: "block", mt: 1 }}
                        >
                            No calendars are available for this event.
                        </Typography>
                    )}
                </Box>

                <Box
                    sx={{
                        mt: 2,
                        p: { xs: 2, sm: 2.5 },
                        borderRadius: 2.5,
                        border: "1px solid",
                        borderColor: "divider",
                        backgroundColor: "background.paper",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            mb: 2,
                        }}
                    >
                        <Box
                            sx={{
                                width: 20,
                                height: 20,
                                borderRadius: "50%",
                                backgroundColor: color,
                                boxShadow:
                                    "0 0 0 3px rgba(20, 35, 25, 0.08)",
                            }}
                        />
                        <Box>
                            <Typography
                                variant="subtitle2"
                                sx={{ fontWeight: 800 }}
                            >
                                Event colour
                            </Typography>
                            <Typography
                                variant="caption"
                                color="text.secondary"
                            >
                                Used to identify the event on calendars.
                            </Typography>
                        </Box>
                    </Box>
                    <Box
                        sx={{
                            "& > div": {
                                width: "100% !important",
                                display: "flex !important",
                                flexWrap: "wrap",
                                gap: "10px",
                            },
                            "& > div > span > div": {
                                margin: "0 !important",
                            },
                        }}
                    >
                        <CirclePicker
                            color={color}
                            colors={eventColors}
                            circleSize={28}
                            circleSpacing={0}
                            width="100%"
                            onChange={(selectedColor) =>
                                setColor(selectedColor.hex)
                            }
                        />
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions
                sx={{
                    px: { xs: 2, sm: 3 },
                    py: 1.5,
                    gap: 1,
                }}
            >
                <Button color="inherit" onClick={handleClose}>
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    disableElevation
                    startIcon={<AddIcon />}
                    onClick={handleAddClick}
                    disabled={!canCreate}
                >
                    Create event
                </Button>
            </DialogActions>
        </Dialog>
    );
}
